import GoogleMapReact from 'google-map-react'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import React from 'react'
import { isMobile } from 'react-device-detect'
import { GrClose } from 'react-icons/gr'
import { IoCloseOutline } from 'react-icons/io5'
import { segment } from 'src/lib/Segments'

import { OIModal } from '@components/UI/organisms'
import ActionButton from '@components/buttons/actionButton'
import emptymap from '@public/emptymap.svg'
import { useAppSelector } from '@redux/hooks'

const GOOGLE_PLACES_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY

const mapContainerStyle = {
  height: 'calc(100vh - 212px)',
  width: '100%',
  borderRadius: '16px',
  overflow: 'hidden',
}

const mapContainerStyleMobile = {
  height: 'calc(100vh - 256px)',
  width: '100%',
  overflow: 'hidden',
}

const center = {
  lat: 40.754159399499386,
  lng: -73.95362145902037,
}

interface PolygonData {
  id: string
  name: string
  paths: { lat: number; lng: number }[]
  children?: PolygonData[]
}

type Props = {
  setNbrModal: (val: boolean) => void
  applyFilter: () => void
  reset: () => void
  updateNeighborhoods: (id: string, name: string, borough: string) => void
  selectedNeighborhoods: any[]
  updateBorough: (name: string) => void
  displayNbr: any
  groupedBoroughs: any
}

function NbrModal({
  setNbrModal,
  applyFilter,
  reset,
  updateNeighborhoods,
  selectedNeighborhoods,
  updateBorough,
  displayNbr,
  groupedBoroughs,
}: Props) {
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [mapsApi, setMapsApi] = useState<any>(null)
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(null)
  const [activePolygon, setActivePolygon] = useState<PolygonData | null>(null)
  const { neighborhoods } = useAppSelector((state) => state.newSearch)
  const [transformedPolygons, setTransformedPolygons] = useState<PolygonData[]>([])
  const [polygonRefs, setPolygonRefs] = useState<google.maps.Polygon[]>([])

  const isValidCoordinate = (coord: number): boolean => {
    return typeof coord === 'number' && !isNaN(coord) && isFinite(coord)
  }

  const transformPolygonData = (data: any[]): PolygonData[] => {
    const transformed: PolygonData[] = []

    // Helper function to recursively add children
    const addChildren = (parent: PolygonData) => {
      const children = data.filter((polygon) => polygon.parentId === parent.id)

      children.forEach((child) => {
        const childData: PolygonData = {
          id: child.id,
          name: child.name,
          paths: [],
        }

        if (child.mapData && child.mapData.polygon) {
          const validPaths = child.mapData.polygon
            .filter(
              ([lng, lat]: [number, number]) => isValidCoordinate(lng) && isValidCoordinate(lat)
            )
            .map(([lng, lat]: [number, number]) => ({ lat, lng }))

          if (validPaths.length > 0) {
            childData.paths = validPaths
          } else {
            console.warn(`No valid coordinates for polygon ID: ${child.id}`)
            return
          }
        } else {
          console.warn(`Polygon data is missing or malformed for polygon ID: ${child.id}`)
          return
        }

        if (!parent.children) {
          parent.children = []
        }

        parent.children.push(childData)
        addChildren(childData)
      })
    }

    // First pass: Create top-level parents and add them to transformed array
    data.forEach((polygon) => {
      if (!polygon.parentId) {
        const parentData: PolygonData = {
          id: polygon.id,
          name: polygon.name,
          paths: [],
        }

        if (polygon.mapData && polygon.mapData.polygon) {
          const validPaths = polygon.mapData.polygon
            .filter(
              ([lng, lat]: [number, number]) => isValidCoordinate(lng) && isValidCoordinate(lat)
            )
            .map(([lng, lat]: [number, number]) => ({ lat, lng }))

          if (validPaths.length > 0) {
            parentData.paths = validPaths
          } else {
            console.warn(`No valid coordinates for polygon ID: ${polygon.id}`)
            return
          }
        } else {
          console.warn(`Polygon data is missing or malformed for polygon ID: ${polygon.id}`)
          return
        }

        transformed.push(parentData)
        addChildren(parentData)
      }
    })

    return transformed
  }

  const calculatePolygonCenter = (paths): { lat: number; lng: number } => {
    let lat = 0,
      lng = 0
    paths.forEach((point) => {
      lat += point.lat
      lng += point.lng
    })
    lat /= paths.length
    lng /= paths.length
    return { lat, lng }
  }

  useEffect(() => {
    const transformedData = transformPolygonData(neighborhoods)
    setTransformedPolygons(transformedData)
  }, [neighborhoods])

  // Effect to render polygons when map and data are ready
  useEffect(() => {
    if (!map || !mapsApi || transformedPolygons.length === 0) return

    // Clear existing polygons
    polygonRefs.forEach((polygon) => polygon.setMap(null))

    const newPolygonRefs: google.maps.Polygon[] = []

    const createPolygon = (polygonData: PolygonData) => {
      if (polygonData.paths.length === 0) return

      const polygon = new mapsApi.Polygon({
        paths: polygonData.paths,
        fillColor: selectedNeighborhoods.some((x) => x.id === polygonData.id)
          ? '#6764A1B2'
          : activePolygon?.id === polygonData.id
            ? '#6764A14D'
            : 'transparent',
        fillOpacity:
          activePolygon?.id === polygonData.id ||
          selectedNeighborhoods.some((x) => x.id === polygonData.id)
            ? 1
            : 0,
        strokeColor: '#6764A1',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        clickable: true,
      })

      polygon.addListener('click', () => handlePolygonClick(polygonData))
      polygon.setMap(map)
      newPolygonRefs.push(polygon)

      // Render children recursively
      if (polygonData.children) {
        polygonData.children.forEach(createPolygon)
      }
    }

    transformedPolygons.forEach(createPolygon)
    setPolygonRefs(newPolygonRefs)

    return () => {
      newPolygonRefs.forEach((polygon) => polygon.setMap(null))
    }
  }, [map, mapsApi, transformedPolygons, selectedNeighborhoods, activePolygon])

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clean up info window
      if (infoWindow) {
        infoWindow.close()
      }
      // Clean up polygons
      polygonRefs.forEach((polygon) => polygon.setMap(null))
    }
  }, [])

  const handlePolygonClick = (polygon: PolygonData) => {
    segment?.listings_filter_allneighborhoods_map_neighborhood()
    if (infoWindow) {
      infoWindow.close()
    }
    setActivePolygon(polygon)

    const center = calculatePolygonCenter(polygon.paths)

    const newInfoWindow = new google.maps.InfoWindow({
      content: `
        <div class="flex space-x-8 items-start">
          <div>
            <div class="text-dark-900 text-sm font-semibold !font-['Nunito']">
              ${polygon.name}
            </div>
            <div class="mt-2 cursor-pointer w-fit text-bluegray-300 text-sm underline !font-['Nunito']" id="select-button-${
              polygon.id
            }">
              ${selectedNeighborhoods.some((p) => p.id === polygon.id) ? 'Unselect' : 'Select'}
            </div>
          </div>
          <div id="close-button-${
            polygon.id
          }" class="-mt-1.5 cursor-pointer w-fit !font-['Nunito'] text-lg">x</div>
        </div>
      `,
      position: center,
    })

    newInfoWindow.addListener('domready', () => {
      document.getElementById(`select-button-${polygon.id}`)?.addEventListener('click', () => {
        selectedNeighborhoods.some((p) => p.id === polygon.id)
          ? segment?.listings_filter_allneighborhoods_map_unselect()
          : segment?.listings_filter_allneighborhoods_map_select()
        handlePolygonSelection(polygon)
        newInfoWindow.close()
        setInfoWindow(null)
        setActivePolygon(null)
      })

      document.getElementById(`close-button-${polygon.id}`)?.addEventListener('click', () => {
        segment?.listings_filter_allneighborhoods_map_cardclose()
        newInfoWindow.close()
        setInfoWindow(null)
        setActivePolygon(null)
      })
    })

    newInfoWindow.open(map)
    setInfoWindow(newInfoWindow)
  }

  const handlePolygonSelection = (polygon: any) => {
    updateNeighborhoods(polygon.id, polygon.name, polygon.borough)
  }

  return (
    <OIModal
      onClose={() => setNbrModal(false)}
      customModalStyle="max-w-5xl w-full"
      customHeader={
        <div className="flex w-full items-baseline justify-between space-x-4 bg-white px-8 py-6 text-xl font-semibold text-dark-900">
          <div className="flex-1 flex flex-col">
            <span className="text-center md:text-left">All Neighborhoods</span>
          </div>
          <GrClose
            className="cursor-pointer"
            color="#6764A1"
            size={16}
            onClick={() => {
              segment?.listings_filter_allneighborhoods_map_goback()
              applyFilter()
              setNbrModal(false)
            }}
          />
        </div>
      }
      closeOnEscape={false}
      closeOnOutsideClick={false}
    >
      <div className="flex h-full flex-col overflow-hidden sm:px-8">
        <div className="flex-1 relative overflow-auto">
          <div className="gap-8 sm:grid sm:grid-cols-2">
            <div className="absolute !bottom-0 z-[400] h-[calc(100vh-580px)] w-full overflow-y-auto rounded-t-2xl !bg-white py-4 px-4 sm:relative sm:h-full sm:py-2 sm:px-0">
              <div className="text-xs font-semibold text-mid-400">YOU SELECTED:</div>
              {selectedNeighborhoods.length > 0 && (
                <div className="flex flex-wrap py-4">
                  {Object.entries(displayNbr).map(([key, value], index) =>
                    (value as any[]).length === groupedBoroughs[key]?.length ? (
                      <div
                        key={index}
                        className="no-wrap mr-2 mb-2 flex items-center justify-between space-x-2 rounded-full border border-blue-75 bg-blue-25 py-1 px-3"
                      >
                        <div className="text-xs text-blue-200">{key}</div>
                        <button
                          onClick={() => {
                            segment?.listings_filter_allneighborhoods_map_unselect()
                            updateBorough(key)
                          }}
                        >
                          <IoCloseOutline size={18} color="#393375" />
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* @ts-expect-error type */}
                        {value.map((x) => (
                          <div
                            key={x.id}
                            className="no-wrap mr-2 mb-2 flex items-center justify-between space-x-2 rounded-full border border-blue-75 bg-blue-25 py-1 px-3"
                          >
                            <div className="text-xs text-blue-200">{x.name}</div>
                            <button
                              onClick={() => {
                                segment?.listings_filter_allneighborhoods_map_unselect()
                                updateNeighborhoods(x.id, x.name, x.borough)
                              }}
                            >
                              <IoCloseOutline size={18} color="#393375" />
                            </button>
                          </div>
                        ))}
                      </>
                    )
                  )}
                </div>
              )}
              {selectedNeighborhoods?.length === 0 && (
                <div className="mt-4 text-center">
                  <Image src={emptymap} alt="emptymap" unoptimized />
                  <div className="pt-2 text-sm text-dark-700">
                    Select neighborhoods on the map and explore listings in all of your favorite
                    areas.
                  </div>
                </div>
              )}
            </div>
            <div style={isMobile ? mapContainerStyleMobile : mapContainerStyle}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: GOOGLE_PLACES_API_KEY,
                  libraries: ['places'],
                  language: 'en-GB',
                }}
                defaultCenter={center}
                defaultZoom={12}
                onGoogleApiLoaded={({ map, maps }) => {
                  setMap(map)
                  setMapsApi(maps)
                }}
                yesIWantToUseGoogleMapApiInternals
                options={{
                  clickableIcons: false,
                  fullscreenControl: false,
                  mapTypeControl: false,
                  streetViewControl: false,
                  zoomControl: true,
                  disableDefaultUI: false,
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-6 py-4 px-4 sm:px-0">
          <div
            className="cursor-pointer text-sm text-bluegray-300 underline"
            onClick={() => {
              segment?.listings_filter_allneighborhoods_map_resetselections()
              reset()
            }}
          >
            Reset
          </div>
          <ActionButton
            label={'Done'}
            size="none"
            customStyle="text-sm px-4 py-1.5"
            onClick={() => {
              segment?.listings_filter_allneighborhoods_map_seeresults()
              applyFilter()
              setNbrModal(false)
            }}
          />
        </div>
      </div>
    </OIModal>
  )
}

export default NbrModal
